import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { evaluateTieredFormulas, normalizeTier } from "../utils/Pricing";

const inventoryCol = (businessId) => collection(db, "businesses", businessId, "inventoryItems");
const inventoryDoc = (businessId, itemId) => doc(db, "businesses", businessId, "inventoryItems", itemId);
const priceHistoryCol = (businessId, itemId) => collection(db, "businesses", businessId, "inventoryItems", itemId, "priceHistory");
const INVENTORY_CATALOG_ID = "catalog";
const inventoryCatalogDoc = (businessId) => inventoryDoc(businessId, INVENTORY_CATALOG_ID);

const CATALOG_BYTE_LIMIT = 900000;

export const toCatalogProduct = (item) => ({
    id: item.id,
    productName: item.productName || "",
    category: item.category || "",
    imageUrl: item.imageUrl || null,
});

export const getInventoryItem = async (businessId, itemId) => {
    if (!businessId || !itemId) return null;
    const snap = await getDoc(inventoryDoc(businessId, itemId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Lightweight product list used by the public form.
 * Returns null when the catalog has not been built yet.
 */
export const getInventoryCatalog = async (businessId) => {
    if (!businessId) return null;
    try {
        const snap = await getDoc(inventoryCatalogDoc(businessId));
        if (!snap.exists()) return null;
        const products = snap.data()?.products;
        return Array.isArray(products) ? products : [];
    } catch (error) {
        console.warn("Inventory catalog unavailable:", error);
        return null;
    }
};

export const writeInventoryCatalog = async (businessId, items) => {
    if (!businessId) return false;
    const products = (items || [])
        .filter((item) => item?.id)
        .map(toCatalogProduct);
    if (JSON.stringify(products).length > CATALOG_BYTE_LIMIT) {
        console.warn("Inventory catalog is too large to store. The form will load the full inventory instead.");
        return false;
    }
    await setDoc(inventoryCatalogDoc(businessId), {
        businessId,
        catalog: true,
        products,
        updatedAt: serverTimestamp(),
    });
    return true;
};

export const rebuildInventoryCatalog = async (businessId) => {
    const items = await listInventoryItems(businessId);
    await writeInventoryCatalog(businessId, items);
    return items.map(toCatalogProduct);
};

const syncCatalogSafely = async (task) => {
    try {
        await task();
    } catch (error) {
        console.warn("Could not update inventory catalog:", error);
    }
};

export const refreshCatalogProduct = async (businessId, itemId) => {
    const catalog = await getInventoryCatalog(businessId);
    if (!catalog) {
        await rebuildInventoryCatalog(businessId);
        return;
    }
    const item = await getInventoryItem(businessId, itemId);
    const products = catalog.filter((product) => product.id !== itemId);
    if (item) products.push(toCatalogProduct(item));
    await writeInventoryCatalog(businessId, products);
};

const removeCatalogProduct = async (businessId, itemId) => {
    const catalog = await getInventoryCatalog(businessId);
    if (!catalog) return;
    await writeInventoryCatalog(
        businessId,
        catalog.filter((product) => product.id !== itemId)
    );
};

const isCatalogDoc = (itemId) => itemId === INVENTORY_CATALOG_ID;

export const listInventoryItems = async (businessId) => {
    const snap = await getDocs(inventoryCol(businessId));
    return snap.docs
        .filter(d => !isCatalogDoc(d.id))
        .map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeInventoryItems = (businessId, onItems) => {
    const q = query(inventoryCol(businessId), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => {
        const fetched = snapshot.docs
            .filter(itemDoc => !isCatalogDoc(itemDoc.id))
            .map(itemDoc => ({ id: itemDoc.id, ...itemDoc.data() }));
        onItems(fetched);
    });
};

const inventoryTemplatesCol = (businessId) => collection(db, "businesses", businessId, "inventoryTemplates");
const inventoryTemplateDoc = (businessId, templateId) => doc(db, "businesses", businessId, "inventoryTemplates", templateId);

export const listInventoryTemplates = async (businessId) => {
    const q = query(inventoryTemplatesCol(businessId), where("businessId", "==", businessId));
    const snap = await getDocs(q);
    return snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(template => !template.deletedAt); // Filter out soft-deleted templates
};

export const getInventoryTemplate = async (businessId, templateId) => {
    const templateDoc = await getDoc(inventoryTemplateDoc(businessId, templateId));
    if (!templateDoc.exists()) {
        return null;
    }
    return { id: templateDoc.id, ...templateDoc.data() };
};

export const saveInventoryTemplate = async (businessId, templateId, payload) => {
    // if templateId provided, update; else create
    if (templateId) {
        await setDoc(inventoryTemplateDoc(businessId, templateId), payload, { merge: true });
        return templateId;
    }
    const ref = await addDoc(inventoryTemplatesCol(businessId), payload);
    // write back generated id if needed
    await setDoc(ref, { templateId: ref.id }, { merge: true });
    return ref.id;
};

export const deleteInventoryTemplate = async (businessId, templateId) => {
    await deleteDoc(inventoryTemplateDoc(businessId, templateId));
};

// Create new inventory item
export const createInventoryItem = async (businessId, payload, options = {}) => {
    const ref = await addDoc(inventoryCol(businessId), payload);
    if (options.syncCatalog !== false) {
        await syncCatalogSafely(() => refreshCatalogProduct(businessId, ref.id));
    }
    return ref.id;
};

// Update existing inventory item (merge)
export const updateInventoryItem = async (businessId, itemId, payload, options = {}) => {
    await setDoc(inventoryDoc(businessId, itemId), payload, { merge: true });
    if (options.syncCatalog !== false) {
        await syncCatalogSafely(() => refreshCatalogProduct(businessId, itemId));
    }
};

// Append a price history entry
export const appendPriceHistory = async (businessId, itemId, entry) => {
    await addDoc(priceHistoryCol(businessId, itemId), entry);
};

// Delete a price history entry
export const deletePriceHistoryEntry = async (businessId, itemId, historyId) => {
    const historyDoc = doc(db, "businesses", businessId, "inventoryItems", itemId, "priceHistory", historyId);
    await deleteDoc(historyDoc);
};

// Delete inventory item
export const deleteInventoryItem = async (businessId, itemId) => {
    await deleteDoc(inventoryDoc(businessId, itemId));
    await syncCatalogSafely(() => removeCatalogProduct(businessId, itemId));
};

// Find all inventory items that use a specific template
export const getInventoryItemsByTemplate = async (businessId, templateId) => {
    const q = query(inventoryCol(businessId), where("templateId", "==", templateId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Update all inventory items that use a template with the new template data
export const updateInventoryItemsFromTemplate = async (businessId, templateId, templateData) => {
    const items = await getInventoryItemsByTemplate(businessId, templateId);
    
    const updates = items.map(async (item) => {
        // Normalize pricing variants
        const normalizedPricingVariants = (templateData.pricingVariants || []).map(normalizeTier);
        
        // Update item with template data while preserving item-specific data
        const updatedPayload = {
            category: templateData.category, // Update category when template category changes
            primaryLabel: templateData.primaryLabel,
            secondaryLabel: templateData.secondaryLabel,
            includeSKU: templateData.includeSKU,
            includeQuantity: templateData.includeQuantity,
            useSecondaryVariant: templateData.useSecondaryVariant,
            pricingMode: "global", // Templates always use global pricing
            pricingVariants: templateData.pricingVariants,
            // Update items array: match by primary variant name and update prices, preserve SKU/quantity
            items: item.items?.map(itemVariant => {
                // Find matching template variant
                const templateVariant = templateData.variants?.find(tv => 
                    tv.primary?.trim().toLowerCase() === itemVariant.primary?.trim().toLowerCase()
                );
                
                // Use template's basePrice if found, otherwise keep existing
                const basePrice = templateVariant ? (templateVariant.basePrice || itemVariant.basePrice || 0) : (itemVariant.basePrice || 0);
                
                // Recalculate prices with new pricing variants
                const evaluated = evaluateTieredFormulas(normalizedPricingVariants, basePrice);
                const updatedPrices = normalizedPricingVariants.map(tier => ({
                    ...tier,
                    price: typeof evaluated?.[tier.id]?.price === "number" ? evaluated[tier.id].price : null
                }));
                
                return {
                    ...itemVariant,
                    basePrice,
                    prices: updatedPrices,
                    // Preserve SKU, quantity, and secondary variants from existing item
                };
            }) || []
        };
        
        await updateInventoryItem(businessId, item.id, updatedPayload, { syncCatalog: false });
    });
    
    await Promise.all(updates);
    await syncCatalogSafely(() => rebuildInventoryCatalog(businessId));
    return items.length;
};


